import axios from 'axios';
import dayjs from 'dayjs';

import { API_ENDPOINTS } from 'Constants';
import { CustomerCharter } from 'Modules/Charter/Types/CustomerCharter';

/**
 * The customer's own charter endpoints.
 *
 * Separate from `CharterApi`, which is the staff schedule's: these two are all a customer can reach,
 * they read and never write, and they answer 404 for a charter that is not theirs or not yet booked.
 *
 * Dates cross the wire as local wall-clock with no timezone (`2026-08-20T09:00:00`), the same as
 * everywhere else a charter is read — parsing one as UTC would move a charter across midnight for
 * any operator not on UTC.
 */

/** A charter as the server sends it — the same shape with its date-times still strings. */
interface CustomerCharterResponse extends Omit<CustomerCharter, 'startDateTime' | 'endDateTime' | 'created'> {
	startDateTime: string;
	endDateTime: string;
	created: string;
}

const fromWire = (charter: CustomerCharterResponse): CustomerCharter => ({
	...charter,
	startDateTime: dayjs(charter.startDateTime).toDate(),
	endDateTime: dayjs(charter.endDateTime).toDate(),
	// Carries an offset, unlike the two above, so this one does move into the reader's zone — it is
	// the instant the charter was written down rather than a time the operator agreed.
	created: dayjs(charter.created).toDate(),
});

/**
 * The signed-in customer's charters, all of them — booked, and ones that were booked and later
 * called off.
 *
 * Reserved charters are not among them: a reserve is a hold on the ferry with nothing agreed behind
 * it, and the server never sends one here.
 */
export async function fetchMyCharters(): Promise<CustomerCharter[]> {
	const res = await axios.get<{ charters: CustomerCharterResponse[] }>(API_ENDPOINTS.CHARTERS.GET_MINE);

	return res.data.charters.map(fromWire);
}

/**
 * One charter, for the customer's charter view — a booked one, or one that was booked and later
 * called off, which the customer can still read.
 *
 * Its own request rather than a read of the list's cache: the address can be opened directly, from
 * an email or a bookmark, without the bookings list ever having loaded.
 */
export async function fetchMyCharter(id: string): Promise<CustomerCharter> {
	const res = await axios.get<CustomerCharterResponse>(API_ENDPOINTS.CHARTERS.GET_MINE_ONE(id));

	return fromWire(res.data);
}
